import { compactWallet } from '../wallet-format.js';
import './player-wallet.css';
import type { HomeData } from '@blockbattle/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Coins, Layers, Plus, Users, X, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isGameRoute, openGameTopUp } from '../game-top-up.js';
import { trackAnalytics } from '../analytics.js';
import { api } from '../api.js';
import { playGameSound } from '../audio.js';
import { formatCountdown } from '../home-state.js';
import { showDiamondShortage } from '../diamond-shortage.js';
import { playGameHaptic } from '../haptics.js';
import { platformShareUrl } from '../share-links.js';
import { useAppStore } from '../store.js';
import type { PathEnergyOffer } from '../api.js';
import { Avatar } from './Avatar.js';
import { DiamondIcon } from './DiamondIcon.js';
import { RewardedEnergyButton } from './RewardedEnergyButton.js';

export function HomePlayerBar({
  className = '',
  user,
  energy,
  context,
  energyOffer,
  readyCards = 0,
}: {
  readonly readyCards?: number;
  readonly className?: string;
  readonly user: HomeData['user'];
  readonly energy: HomeData['energy'];
  readonly context?: {
    readonly kind: 'PATH';
    readonly stars: number;
  };
  readonly energyOffer?: PathEnergyOffer | undefined;
}) {
  const client = useQueryClient();
  const location = useLocation();
  const adapter = useAppStore((state) => state.adapter);
  const haptics = useAppStore((state) => state.haptics);
  const previousCurrency = useRef(user.currency);
  const [rewardPulse, setRewardPulse] = useState(false);
  const [coinsOpen, setCoinsOpen] = useState(false);
  const [energyOpen, setEnergyOpen] = useState(false);
  useEffect(() => {
    if (!coinsOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCoinsOpen(false);
    };
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Element && !event.target.closest('.home-player-bar'))
        setCoinsOpen(false);
    };
    document.addEventListener('keydown', close);
    document.addEventListener('pointerdown', outside);
    return () => {
      document.removeEventListener('keydown', close);
      document.removeEventListener('pointerdown', outside);
    };
  }, [coinsOpen]);

  useEffect(() => {
    const open = () => setEnergyOpen(true);
    window.addEventListener('blockbattle:open-energy', open);
    return () => window.removeEventListener('blockbattle:open-energy', open);
  }, []);
  const [actionError, setActionError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const buyEnergy = useMutation({
    mutationFn: () => api.buyEnergy(crypto.randomUUID()),
    onSuccess: async (result) => {
      setActionError(`+${result.energyGranted} энергии начислено`);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['path'] }),
        client.invalidateQueries({ queryKey: ['home'] }),
      ]);
      playGameSound('purchase');
      playGameHaptic(adapter, haptics, 'energy');
    },
    onError: (error) => {
      setActionError(error instanceof Error ? error.message : 'Не удалось купить энергию.');
      playGameHaptic(adapter, haptics, 'error');
    },
  });
  useEffect(() => {
    if (user.currency <= previousCurrency.current) {
      previousCurrency.current = user.currency;
      return;
    }
    previousCurrency.current = user.currency;
    setRewardPulse(true);
    const timer = window.setTimeout(() => setRewardPulse(false), 650);
    return () => window.clearTimeout(timer);
  }, [user.currency]);

  useEffect(() => {
    if (!energyOpen) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [energyOpen]);

  const openEnergy = () => {
    setActionError(null);
    setEnergyOpen(true);
    trackAnalytics('energy_panel_opened', {
      energy: energy.current,
      referralRemaining: energy.referral.remainingToday,
    });
    playGameSound('select');
    playGameHaptic(adapter, haptics, 'select');
  };

  const invite = async () => {
    try {
      setActionError(null);
      trackAnalytics('energy_referral_shared', { platform: adapter?.platform ?? 'unknown' });
      if (adapter?.platform === 'max') {
        const card = await api.maxReferralCard();
        await adapter.openExternal(card.botUrl);
      } else {
        await adapter?.share({
          text: `Сыграй со мной в БлокБитву: получим по 20 монет пути, а я — +${energy.referral.rewardPerFriend} ед. энергии!`,
          url: await platformShareUrl(adapter, `ref_${user.id}`),
          mode: 'invite',
        });
      }
      playGameSound('reward');
      playGameHaptic(adapter, haptics, 'reward');
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Не удалось открыть приглашение.');
      playGameHaptic(adapter, haptics, 'error');
    }
  };

  const countdown = formatCountdown(energy.nextEnergyAt, now);
  return (
    <>
      <div
        className={`home-player-bar ${className} ${context?.kind === 'PATH' ? 'is-path-context' : ''}`.trim()}
      >
        <Link
          className="home-player-identity"
          to="/profile"
          aria-label={`Открыть профиль: ${user.name}`}
          onClick={() => {
            playGameSound('navigate');
            playGameHaptic(adapter, haptics, 'navigate');
          }}
        >
          <Avatar name={user.name} url={user.avatarUrl} size={42} social={user.social} />
          <div className="home-player-name">
            <strong title={user.name}>{user.name}</strong>
            <span>Уровень {user.careerLevel}</span>
          </div>
        </Link>
        <button
          className="home-energy-control"
          type="button"
          aria-label={`Энергия ${energy.current} из ${energy.max}. Получить дополнительную энергию`}
          onClick={openEnergy}
        >
          <span className="home-player-stat energy">
            <Zap aria-hidden="true" />
            <b>
              {energy.current}/{energy.max}
            </b>
          </span>
          <span className="home-energy-plus" aria-hidden="true">
            <Plus />
          </span>
        </button>
        <button
          className="home-coins-control"
          type="button"
          aria-label={`Монеты пути: ${(user.pathCoins ?? 0).toLocaleString('ru-RU')}. Показать баланс`}
          aria-expanded={coinsOpen}
          onClick={() => setCoinsOpen(!coinsOpen)}
        >
          <Coins size={13} aria-hidden="true" />
          <b>{compactWallet(user.pathCoins ?? 0)}</b>
        </button>
        <Link
          className={`home-diamond-control ${rewardPulse ? 'reward-pulse' : ''}`}
          to="/store?view=gems"
          aria-label={`Купить алмазы. Баланс ${user.currency.toLocaleString('ru-RU')}`}
          onClick={(event) => {
            trackAnalytics('gem_store_opened', { source: 'player_bar' });
            if (isGameRoute(location.pathname)) {
              event.preventDefault();
              openGameTopUp();
            }
          }}
        >
          <span
            className="home-player-stat currency"
            title={`Алмазы: ${user.currency.toLocaleString('ru-RU')}`}
            aria-live="polite"
          >
            <DiamondIcon />
            <b>{compactWallet(user.currency)}</b>
            <span className="sr-only">алмазов</span>
          </span>
          <span className="home-diamond-plus" aria-hidden="true">
            <Plus />
          </span>
        </Link>
        {coinsOpen && (
          <aside className="header-wallet-detail" aria-label="Баланс кошелька">
            <button type="button" aria-label="Закрыть баланс" onClick={() => setCoinsOpen(false)}>
              ×
            </button>
            <b>{(user.pathCoins ?? 0).toLocaleString('ru-RU')} монет пути</b>
            <span>{user.currency.toLocaleString('ru-RU')} алмазов</span>
            <small>Монеты можно потратить на артефакты.</small>
            {!isGameRoute(location.pathname) && (
              <Link to="/store?view=artifacts" onClick={() => setCoinsOpen(false)}>
                В мастерскую ›
              </Link>
            )}
          </aside>
        )}
        {readyCards > 0 ? (
          <Link
            className="header-card-count"
            to="/cards"
            aria-label={`Карточки: ${readyCards} ждут открытия`}
            title={`${readyCards} карточек ждут открытия`}
          >
            <Layers size={15} aria-hidden="true" />
            <b>{readyCards > 99 ? '99+' : readyCards}</b>
          </Link>
        ) : null}
      </div>

      {energyOpen ? (
        <div
          className="modal-backdrop energy-modal-backdrop"
          onMouseDown={() => setEnergyOpen(false)}
        >
          <section
            className="app-modal energy-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="energy-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="modal-header">
              <div>
                <h2 id="energy-modal-title">
                  Энергия {energy.current}/{energy.max}
                </h2>
              </div>
              <button
                className="icon-button"
                aria-label="Закрыть"
                onClick={() => setEnergyOpen(false)}
              >
                <X />
              </button>
            </header>
            <p className="energy-modal-summary">
              {energyOffer?.available
                ? `+${energyOffer.amount} за ${energyOffer.diamondPrice} алмазов · бесплатно ${countdown ? `через ${countdown}` : 'за рекламу или друга'}.`
                : `+1 за рекламу или друга${countdown ? `; бесплатно через ${countdown}` : ''}.`}
            </p>
            <div className="energy-modal-actions">
              {energyOffer?.available ? (
                user.currency >= energyOffer.diamondPrice ? (
                  <button
                    className="primary-button energy-buy-button"
                    disabled={buyEnergy.isPending}
                    onClick={() => buyEnergy.mutate()}
                  >
                    <Zap />
                    {buyEnergy.isPending
                      ? 'Покупаем…'
                      : `Купить +${energyOffer.amount} · ${energyOffer.diamondPrice}`}
                    <DiamondIcon aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    className="primary-button energy-buy-button"
                    type="button"
                    onClick={() => {
                      setEnergyOpen(false);
                      showDiamondShortage({ source: 'energy_purchase' });
                    }}
                  >
                    <DiamondIcon /> Получить алмазы
                  </button>
                )
              ) : null}
              <RewardedEnergyButton ads={energy.ads} />
              <button
                className="secondary-button energy-invite-button"
                onClick={() => void invite()}
              >
                <Users /> Пригласить +{energy.referral.rewardPerFriend}
              </button>
            </div>
            {actionError ? <p className="form-error">{actionError}</p> : null}
          </section>
        </div>
      ) : null}
    </>
  );
}
